import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import PublicHeader from '../../components/ui/PublicHeader';
import PublicFooter from '../../components/ui/PublicFooter';
import Icon from '../../components/AppIcon';
import { followupStatus } from '../../api/followup';
import FollowupStatusCard from './components/FollowupStatusCard';
import FollowupCodeInput from './components/FollowupCodeInput';

const Followup = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submittedTid, setSubmittedTid] = useState(null);
  const [retryKey, setRetryKey] = useState(0);

  const urlTid = useMemo(() => {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get('tid');
  }, []);

  // URL ?tid= takes precedence; otherwise use user-submitted code
  const effectiveTid = urlTid || submittedTid;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!effectiveTid) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    const fetchStatus = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await followupStatus(effectiveTid);
        if (!cancelled) {
          setData(result);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            'دریافت اطلاعات پیگیری با مشکل مواجه شد. لطفاً دوباره تلاش کنید.',
          );
          console.error('Followup status fetch failed:', err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchStatus();

    return () => {
      cancelled = true;
    };
  }, [effectiveTid, retryKey]);

  const handleRetry = () => {
    setError(null);
    setRetryKey((k) => k + 1);
  };

  const transaction = data?.transaction;
  const msgReq = data?.msg_req;
  const refundReq = data?.refund;
  const refundable = data?.refundable;

  return (
    <>
      <Helmet>
        <title>پیگیری پیام - Khejalatiam</title>
        <meta
          name="description"
          content="وضعیت ارسال پیام خود را پیگیری کنید"
        />
      </Helmet>
      <div
        className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50"
        dir="rtl"
      >
        <PublicHeader />

        <main className="container mx-auto px-4 py-8 max-w-4xl">
          {!effectiveTid ? (
            <FollowupCodeInput onSubmit={setSubmittedTid} />
          ) : loading ? (
            <div className="flex flex-col items-center justify-center py-20 space-y-6">
              <div className="h-12 w-12 rounded-full border-4 border-t-transparent animate-spin border-purple-500" />
              <p className="text-lg text-gray-600">در حال دریافت اطلاعات...</p>
            </div>
          ) : error ? (
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center space-y-4">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-red-100">
                <Icon name="AlertCircle" size={32} color="#dc2626" />
              </div>
              <p className="text-lg text-gray-700">{error}</p>
              <button
                type="button"
                onClick={handleRetry}
                className="inline-flex items-center justify-center gap-2 px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold transition-all"
              >
                تلاش مجدد
              </button>
            </div>
          ) : (
            <FollowupStatusCard
              transaction={transaction}
              msgReq={msgReq}
              refundReq={refundReq}
              tid={effectiveTid}
              refundable={refundable}
            />
          )}
        </main>

        <PublicFooter />
      </div>
    </>
  );
};

export default Followup;
